import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Image from 'next/image'
import { useSearchPois } from '../queries/searchPois'
import { useSearchParcours } from '../queries/searchParcours'
import { useSearchAtmosphericAreas } from '../queries/searchAtmosphericAreas'
import { DEVICE_MD_UP } from '../utils/deviceUtils'
import PoiFilters from './PoiFilters'
import FeedbackFormLink from './FeedbackFormLink'
import { OverlayLayers } from '../constants'

const initialHeader = 'Uitgelicht...'
const initialError = ''
const initialSearchQuery = ''

const SearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem 0.5rem 0.25rem;
  background-color: white;
  margin: 0.25rem;
  border-radius: 3px;
  cursor: pointer;
  box-shadow: ${({ shadow, theme }) => (shadow ? theme.defaultBoxShadow : 'none')};
`

const SearchInput = styled.div`
  display: flex;
  flex-direction: row;
  border: 1px solid transparent;
  flex-grow: 2;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;

  &:focus-within {
    outline: 0;
    border: 1px solid ${({ theme }) => theme.colors.primary};
  }

  input:focus {
    outline: none;
  }

  button {
    background-color: transparent;
    border-radius: 50%;
    border: none;
    padding: 0 5px;
    box-shadow: ${({ theme }) => theme.defaultShadow};
  }
`

const SearchMenu = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  top: ${({ isOpen }) => (isOpen ? 0 : '0.25rem')};
  z-index: ${({ theme }) => theme.zindex.overlay};
  background-color: ${({ isOpen }) => (isOpen ? 'white' : 'none')};
  height: ${({ isOpen }) => (isOpen ? '80vh' : '')};
  border-radius: 3px;
  left: ${({ isOpen }) => (isOpen ? '0' : '50%')};
  transform: ${({ isOpen }) => (isOpen ? '0' : 'translateX(-50%)')};
  width: 100%;
  box-shadow: ${({ isOpen, theme }) => (isOpen ? theme.defaultBoxShadow : 'none')};

  @media ${DEVICE_MD_UP} {
    width: 400px;
    min-height: ${({ isOpen }) => (isOpen ? '100vh' : '')};
  }
`

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  min-height: 0;
`

const MenuList = styled.ul`
  list-style-type: none;
  text-align: left;
  margin: 1rem 0;
  overflow-y: auto;

  li:not(:last-child) {
    margin-bottom: 0.5rem;
  }
`

const MenuHeader = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.25rem;
  font-weight: bold;
  text-align: left;
`

const MenuItem = styled.li`
  cursor: pointer;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 1rem;

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const SearchBar = ({
  value,
  setValue,
  placeholder,
  handleClick,
  handleSearch,
  returnButton,
  deleteButton,
}) => {
  const handleChange = (e) => {
    setValue(e.target.value)
  }

  const onSubmit = (e) => {
    if ((e.key === 'Enter' || !e.key) && value) {
      handleSearch(value)
    }
  }

  return (
    // we use the presence of the returnButton to determine if the searchBar is open
    // based on this we add shadow and an onClick handler or not.
    <SearchContainer shadow={!returnButton}>
      {returnButton}
      <SearchInput>
        <input
          className='flex-grow-1 border-0'
          onClick={!returnButton ? handleClick : undefined}
          onKeyDown={onSubmit}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
        />
        {deleteButton}
      </SearchInput>
      <button className='d-inline-flex border-0 bg-transparent' onClick={onSubmit}>
        <Image src='/assets/icons/search.svg' width={16} height={16} />
      </button>
    </SearchContainer>
  )
}

const MenuContent = ({
  menuCategories,
  setMenuCategories,
  menuPois,
  setMenuPois,
  setPoiContent,
  header,
  setHeader,
  handleClose,
  poiLayers,
  baseMap,
}) => {
  const handleLayerClick = ({ target }) => {
    const selectedLayer = menuCategories.filter((poiLayer) => poiLayer.id === target.id)[0]
    setHeader(selectedLayer.name)
    const pois = []
    selectedLayer.dataLayer.forEach((poi) => pois.push(poi))
    setMenuPois(pois)
    setMenuCategories([])
  }

  const handlePoiClick = ({ target }) => {
    const layer = poiLayers.filter((poiLayer) => poiLayer.name === header)[0]
    layer.dataLayer.getMap() === null && layer.dataLayer.setMap(baseMap)
    handleClose()
    setPoiContent(menuPois[target.id])
  }

  return (
    <>
      {menuCategories?.map((category) => (
        <MenuItem key={category.id} id={category.id} onClick={handleLayerClick} aria-hidden='true'>
          {category.name}
        </MenuItem>
      ))}
      {menuPois?.map((poi, idx) => (
        <MenuItem key={idx} id={idx} onClick={handlePoiClick} aria-hidden='true'>
          {poi.getProperty('name')}
          <Image aria-hidden='true' src='/assets/icons/arrow-forward.svg' width={20} height={20} />
        </MenuItem>
      ))}
    </>
  )
}

const parseSearchResults = (results, query, newMenuPois, poiLayers) => {
  results.map((result) => {
    const layer = poiLayers.filter((poiLayer) => poiLayer.name === result.name)[0]
    layer.dataLayer.forEach(
      (poi) =>
        (poi.getProperty('name').toLowerCase().includes(query.toLowerCase()) ||
          poi.getProperty('description')?.toLowerCase().includes(query.toLowerCase())) &&
        newMenuPois.push(poi)
    )
  })
}

const ContactLink = ({ feedbackForm }) => (
  <MenuItem>
    <FeedbackFormLink href={feedbackForm}>
      <Image src='/assets/icons/flag.svg' width={16} height={16} />
      <span className='ms-2'>Geef feedback over de toepassing</span>
    </FeedbackFormLink>
  </MenuItem>
)

const MenuSection = ({ header, children }) => (
  <>
    <MenuList>
      <MenuHeader>{header}</MenuHeader>
      {children}
    </MenuList>
  </>
)

const Proclaimer = ({ proclaimerForm }) => (
  <MenuItem>
    <FeedbackFormLink href={proclaimerForm}>
      <Image src='/assets/icons/check.svg' width={16} height={16} />
      <span className='ms-2'>Actuele en juiste informatie</span>
    </FeedbackFormLink>
  </MenuItem>
)

const SearchArea = ({ isOpen, event, baseMap, poiLayers, setPoiContent, handleOverlayChange }) => {
  const [menuCategories, setMenuCategories] = useState(poiLayers)
  const [menuPois, setMenuPois] = useState([])
  const [header, setHeader] = useState(initialHeader)
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery)
  const [errorMessage, setErrorMessage] = useState(initialError)

  useEffect(() => {
    if (poiLayers !== menuCategories) {
      setMenuCategories(poiLayers)
    }
  }, [poiLayers])

  const toggleSetShowSearchOverlay = () => {
    handleOverlayChange(!errorMessage && isOpen ? OverlayLayers.NONE : OverlayLayers.SEARCH)
    resetValues()
  }

  const resetValues = () => {
    if (searchQuery) {
      setSearchQuery(initialSearchQuery)
    }
    setErrorMessage(initialError)
    setMenuPois([])
    setHeader(initialHeader)
    setMenuCategories(poiLayers)
  }

  const handleSearch = async () => {
    try {
      const poiSearchResult = await useSearchPois(searchQuery)
      const parcoursSearchResult = await useSearchParcours(searchQuery)
      const atmosphericAreaSearchResult = await useSearchAtmosphericAreas(searchQuery)
      if (
        !poiSearchResult.length &&
        !parcoursSearchResult.length &&
        !atmosphericAreaSearchResult.length
      ) {
        setErrorMessage('Geen resultaten')
      } else {
        const newMenuPois = []
        parseSearchResults(parcoursSearchResult, searchQuery, newMenuPois, poiLayers)
        parseSearchResults(atmosphericAreaSearchResult, searchQuery, newMenuPois, poiLayers)
        parseSearchResults(poiSearchResult, searchQuery, newMenuPois, poiLayers)
        const numResults = newMenuPois.length
        setErrorMessage(initialError)
        setHeader(numResults === 1 ? `${numResults} resultaat` : `${numResults} resultaten`)
        setMenuCategories([])
        setMenuPois(newMenuPois)
      }
    } catch (error) {
      setErrorMessage('Er ging wat mis, probeer het nog eens.')
    }
  }

  return (
    <>
      <SearchMenu isOpen={isOpen}>
        {false && (
          <SearchBar
            value={searchQuery}
            setValue={setSearchQuery}
            placeholder={`Zoeken in ${event.name}`}
            handleClick={toggleSetShowSearchOverlay}
            handleSearch={handleSearch}
            returnButton={
              isOpen && (
                <button
                  title={header === initialHeader ? 'Sluit zoekvenster' : 'Een stap terug'}
                  className='d-inline-flex border-0 bg-transparent'
                  onClick={header === initialHeader ? toggleSetShowSearchOverlay : resetValues}
                >
                  <Image
                    alt='Icoon van pijl naar links'
                    src='/assets/icons/arrow-back.svg'
                    width={20}
                    height={20}
                  />
                </button>
              )
            }
            deleteButton={
              searchQuery && (
                <button
                  title='Zoeken'
                  className='d-inline-flex align-items-center'
                  onClick={resetValues}
                >
                  <Image
                    alt='Icoon van een vergrootglas'
                    src='/assets/icons/no-check.svg'
                    width={10}
                    height={10}
                  />
                </button>
              )
            }
          />
        )}
        {isOpen && (
          <MenuContainer>
            {errorMessage && <MenuSection header=''>{errorMessage}</MenuSection>}
            {!errorMessage && (
              <MenuSection header={header}>
                <MenuContent
                  menuCategories={menuCategories}
                  setMenuCategories={setMenuCategories}
                  menuPois={menuPois}
                  setMenuPois={setMenuPois}
                  setPoiContent={setPoiContent}
                  header={header}
                  setHeader={setHeader}
                  handleClose={toggleSetShowSearchOverlay}
                  poiLayers={poiLayers}
                  baseMap={baseMap}
                />
              </MenuSection>
            )}
            {header === initialHeader && (
              <div>
                <MenuSection header='Contact'>
                  <ContactLink feedbackForm={event.feedbackForm.general} />
                </MenuSection>
                <MenuSection header='Proclaimer'>
                  <Proclaimer proclaimerForm={event.proclaimerForm} />
                </MenuSection>
              </div>
            )}
          </MenuContainer>
        )}
      </SearchMenu>
      {!isOpen && <PoiFilters baseMap={baseMap} poiLayers={poiLayers} />}
    </>
  )
}

export default SearchArea
